import { useEffect, useRef, useState } from 'react'
import { copy, type Locale } from './content'
import { detectLocale, saveLocale } from './lib/locale'
import { readCapabilities } from './lib/capabilities'
import { trackConversion } from './lib/analytics'
import { Hero } from './components/Hero'
import { LeadForm } from './components/LeadForm'
import { NavbarBlock } from './components/blocks/NavbarBlock'
import { OfferStripBlock } from './components/blocks/OfferStripBlock'
import { PricingBlock } from './components/blocks/PricingBlock'
import { ProcessBlock } from './components/blocks/ProcessBlock'
import { FaqBlock } from './components/blocks/FaqBlock'
import { ContactBlock } from './components/blocks/ContactBlock'
import { FooterBlock } from './components/blocks/FooterBlock'

export function App() {
  const [locale, setLocale] = useState<Locale>(() => detectLocale(window.localStorage, navigator.language))
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const [capability] = useState(() => readCapabilities())
  const t = copy[locale]
  const motionEnabled = capability === 'modern'

  useEffect(() => {
    const meta = locale === 'el'
      ? { title: 'KÍMA. Ιστοσελίδες για τοπικές επιχειρήσεις στην Κύπρο', description: 'Δίγλωσσες ιστοσελίδες για τοπικές επιχειρήσεις στην Κύπρο. Google Maps, κλήση και WhatsApp. Δείτε την αρχική σας πριν πληρώσετε.' }
      : { title: 'KÍMA. Local business websites in Cyprus', description: 'A Greek-and-English website for local businesses in Cyprus, with Google Maps, click-to-call and WhatsApp. See your homepage before you pay.' }
    document.documentElement.lang = locale
    document.title = meta.title
    document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description)
    saveLocale(window.localStorage, locale)
  }, [locale])

  useEffect(() => {
    if (!menuOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMenuOpen(false); menuButtonRef.current?.focus() }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [menuOpen])

  return <div className="site">
    <a className="skip-link" href="#top">Μετάβαση στο περιεχόμενο / Skip to content</a>
    <NavbarBlock
      brand="KÍMA / local websites in Cyprus"
      links={[{ href: '#plans', label: t.nav.plans }, { href: '#process', label: t.nav.process }, { href: '#faq', label: t.nav.faq }]}
      cta={t.nav.cta}
      localeCode={locale === 'el' ? 'ΕΛ / EN' : 'EN / ΕΛ'}
      localeLabel={t.nav.languageLabel}
      navigationLabel={locale === 'el' ? 'Κύρια πλοήγηση' : 'Primary navigation'}
      mobileNavigationLabel={locale === 'el' ? 'Κινητή πλοήγηση' : 'Mobile navigation'}
      menuLabel={locale === 'el' ? 'Άνοιγμα μενού' : 'Open menu'}
      closeMenuLabel={locale === 'el' ? 'Κλείσιμο μενού' : 'Close menu'}
      menuOpen={menuOpen}
      menuButtonRef={menuButtonRef}
      onToggleLocale={() => setLocale(locale === 'el' ? 'en' : 'el')}
      onToggleMenu={() => setMenuOpen((open) => !open)}
      onCloseMenu={() => setMenuOpen(false)}
      onCtaClick={() => trackConversion('header_cta_click')}
    />

    <main id="top">
      <Hero t={t} />
      <OfferStripBlock facts={t.facts} />
      <PricingBlock copy={t.pricing} devPolicy={locale === 'el' ? 'ΔΟΚΙΜΑΣΤΙΚΟ: ακύρωση/επιστροφή — συμπληρώστε εγκεκριμένους όρους πριν τη δημοσίευση.' : 'TEST ONLY: cancellation/refund — replace with approved terms before publishing.'} onCtaClick={() => trackConversion('pricing_cta_click')} />
      <ProcessBlock copy={t.process} motionEnabled={motionEnabled} />
      <FaqBlock copy={t.faq} />
      <ContactBlock copy={t.finalCta}><LeadForm t={t.form} locale={locale} /></ContactBlock>
    </main>
    <FooterBlock copy={t.footer} />
  </div>
}
